import { 
  Box, 
  Button, 
  IconButton, 
  Table as MUITable, 
  TableCell, 
  TableContainer, 
  TableRow, 
  Typography, 
  Menu, 
  MenuItem, 
  Tooltip,
  styled
} from '@mui/material';

// Table container styled components
export const TableWrapper = styled(Box)(({ theme }) => ({
  minHeight: '200px',
  padding: '24px',
  background: 'white',
  borderRadius: '6px',
  boxShadow: '0px 0px 4px 0px rgba(0,0,0,0.08)',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  height: '100%',
  [theme.breakpoints.down('sm')]: {
    padding: '16px',
    gap: '16px',
    minHeight: 'unset',
  },
}));

export const TableTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.375rem',
  fontWeight: 700,
  lineHeight: '1.56',
  color: 'rgb(7, 49, 66)',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.25rem',
  },
}));

export const StyledTableContainer = styled(TableContainer)<{ maxWidth?: string | number }>(({ theme, maxWidth }) => ({
  height: '100%',
  flexGrow: 1,
  overflowY: 'auto',
  overflowX: 'auto',
  width: '100%',
  maxWidth: maxWidth || '100%',
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#888',
    borderRadius: '4px',
  },
  position: 'relative',
  scrollPaddingRight: '120px',
  [theme.breakpoints.down('sm')]: {
    maxHeight: '400px',
  },
}));

// Table elements styled components
export const StyledTable = styled(MUITable)({
  tableLayout: 'auto',
  width: 'fit-content',
  minWidth: '100%',
  borderSpacing: '4px',
  borderCollapse: 'separate',
});

export const StyledTableCell = styled(TableCell)<{ width?: string }>(({ theme, width }) => ({
  padding: '8px 12px',
  background: '#E7EFF5',
  borderRadius: '4px',
  fontFamily: "'Lato', sans-serif",
  fontSize: '12px',
  fontWeight: 700,
  color: 'rgba(0, 0, 0, 0.8)',
  textAlign: 'center',
  border: 'none',
  height: '32px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  whiteSpace: 'nowrap',
  width: width || 'auto',
  minWidth: width || 'auto',
  maxWidth: width || 'none',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  position: 'relative',
  '&.star-cell': {
    width: '40px',
    minWidth: '40px',
    maxWidth: '40px',
    padding: '8px',
  },
  '&.actions-cell': {
    width: '120px',
    minWidth: '120px',
    maxWidth: '120px',
    justifyContent: 'flex-end',
    position: 'sticky',
    right: 0,
    zIndex: 2,
    background: '#E7EFF5',
    boxShadow: '-4px 0 8px rgba(0, 0, 0, 0.1)',
  },
  '&.todo-cell': {
    justifyContent: 'flex-start',
    textAlign: 'left',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '6px 8px',
    fontSize: '11px',
    height: '28px',
    '&.actions-cell': {
      width: '100px',
      minWidth: '100px',
      maxWidth: '100px',
    }
  }
}));

export const StyledTableRow = styled(TableRow)<{ $clickable?: boolean }>(({ theme, $clickable }) => ({
  display: 'flex',
  gap: '4px',
  marginBottom: '2px',
  position: 'relative',
  cursor: $clickable ? 'pointer' : 'default',
  width: 'fit-content',
  '&:hover': {
    background: '#fafafa',
  },
  '& td': {
    background: 'transparent',
    border: 'none',
    padding: '8px 12px',
    fontSize: '12px',
    fontWeight: 400,
    textAlign: 'center',
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  '& td[data-text-align="right"]': {
    justifyContent: 'flex-end',
    textAlign: 'right',
  },
  '& td[data-text-align="left"]': {
    justifyContent: 'flex-start',
    textAlign: 'left',
  },
  '& td.actions-cell': {
    background: '#E7EFF5',
    boxShadow: '-4px 0 8px rgba(0, 0, 0, 0.1)',
  },
    '&.bold-row': {
    '& td': {
      fontWeight: '700 !important',
      color: '#063142',
      '& div': {
        fontWeight: '700 !important',
      }
    },
  },
  '&.alternate': {
    background: '#F5F5F5',
    '& td.actions-cell': {
      background: '#E7EFF5',
    }
  },
  [theme.breakpoints.down('sm')]: {
    gap: '2px',
    marginBottom: '1px',
    '& td': {
      padding: '6px 8px',
      fontSize: '11px',
    }
  }
}));

// Buttons and interactive elements
export const ShowAllButton = styled(Button)(({ theme }) => ({
  height: '28px',
  padding: '0 24px',
  background: '#E7EFF5',
  borderRadius: '9999px',
  fontSize: '12px',
  fontWeight: 700,
  fontFamily: "'Lato', sans-serif",
  textTransform: 'uppercase',
  color: '#1A1A1A',
  '&:hover': {
    background: '#d0e0ed',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '0 16px',
    fontSize: '11px',
    height: '24px',
    whiteSpace: 'nowrap',
  },
}));

export const PaginationButton = styled(Button)(({ theme }) => ({
  padding: '8px 16px',
  border: '1px solid #1A1A1A',
  borderRadius: '4px',
  color: 'rgba(0, 0, 0, 0.8)',
  fontSize: '14px',
  fontFamily: "'Lato', sans-serif",
  textTransform: 'none',
  minWidth: 'unset',
  [theme.breakpoints.down('sm')]: {
    padding: '4px 8px',
    fontSize: '12px',
  },
}));

export const ActionIconButton = styled(IconButton)(({ theme }) => ({
  width: '24px',
  height: '24px',
  padding: 0,
  color: 'rgba(0, 0, 0, 0.8)',
  '&:hover': {
    background: 'rgba(0, 0, 0, 0.04)',
  },
  [theme.breakpoints.down('sm')]: {
    width: '20px',
    height: '20px',
  },
}));

// Menus, tooltips, and overlays
export const SortMenu = styled(Menu)({
  '& .MuiPaper-root': {
    minWidth: '250px',
    maxWidth: '300px',
    boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
    borderRadius: '8px',
  }
});

export const SortMenuItem = styled(MenuItem)({
  fontSize: '12px',
  fontFamily: "'Lato', sans-serif",
  padding: '10px 16px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  '& .MuiSvgIcon-root': {
    fontSize: '16px',
  },
  '&:hover': {
    backgroundColor: '#f5f5f5',
  }
});

export const StyledTooltip = styled(Tooltip)(({ theme }) => ({
  tooltip: {
    backgroundColor: '#333',
    color: 'white',
    fontSize: '12px',
    padding: '8px 12px',
    borderRadius: '4px',
    maxWidth: '200px',
    wordBreak: 'break-word'
  },
  arrow: {
    color: '#333'
  }
}));

// Helper functions
export const calculateTextWidth = (text: string, font: string): number => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) return 0;
  
  context.font = font;
  const metrics = context.measureText(text);
  return metrics.width;
};

export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  
  return function(...args: Parameters<T>) {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

export const truncateText = (text: string, maxLength: number = 4): string => {
  if (!text) return '';
  return text.length > maxLength ? text.substring(0, maxLength) + '.' : text;
};
